/**
 * NavStackProvider (navigation overhaul, chunk A1).
 *
 * Mounted INSIDE <BrowserRouter> (App.tsx) so it can use useNavigate /
 * useLocation, and above every page so PrayPage / PrayerDetail / Garden can
 * register layers. The coordinator (src/lib/nav-stack.ts) is created ONCE and
 * reads navigate/location through refs.
 *
 * Reconciler (§3.3): one layout effect on `location` compares
 * browserDepth = location.state.navDepth vs the synchronous stack length.
 *   equal -> no-op (self-push/close echoes land here)
 *   <     -> removeToDepth(browserDepth)  (BACK / swipe / in-app close)
 *   >     -> forward-neutralize (decision #2, no reopen), reconcilingRef-guarded
 *
 * Cold-mount (§3.4): normalize a stale navDepth>0 with replace; normalizedRef
 * flips true only after that replace's echo lands (browserDepth 0 vs empty
 * stack), so the reconciler can never mis-fire FORWARD on the stale first read.
 * The replace is issued from a PASSIVE effect: BrowserRouter subscribes to
 * history in its own layout effect, which runs after this child's, so a
 * navigate() from a layout effect at mount is written to browser history but
 * never delivered as a location change, and the echo never lands.
 *
 * Split from useNavLayer.ts (which owns the context + hooks) so each file's
 * exports stay single-kind (react-refresh/only-export-components).
 */
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createNavStack, type NavStack } from '@/lib/nav-stack';
import { NavStackContext } from '@/hooks/useNavLayer';

function navDepthOf(state: unknown): number {
  return (state as { navDepth?: number } | null)?.navDepth ?? 0;
}

export function NavStackProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Refs so the once-created coordinator + the reconciler always read the
  // latest navigate/location without re-creating the coordinator or making the
  // reconciler effect depend on navigate identity.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const locationRef = useRef(location);
  locationRef.current = location;

  const coordRef = useRef<NavStack | null>(null);
  if (coordRef.current === null) {
    coordRef.current = createNavStack(() => ({
      navigate: (to, opts) =>
        (navigateRef.current as (to: unknown, opts?: unknown) => void)(to, opts),
      getSamePath: () => {
        const l = locationRef.current;
        return l.pathname + l.search + l.hash;
      },
      getLocationState: () =>
        locationRef.current.state as Record<string, unknown> | null | undefined,
    }));
  }
  const coord = coordRef.current;

  const normalizedRef = useRef(false);
  const reconcilingRef = useRef(false);
  const didColdMountRef = useRef(false);
  const pendingNormalizeRef = useRef(false);

  // Cold-mount normalization (runs once, before the reconciler acts on a stale
  // depth). Declared BEFORE the reconciler effect so it runs first on mount.
  useLayoutEffect(() => {
    if (didColdMountRef.current) return;
    didColdMountRef.current = true;
    coord.resetStack();
    const depth = navDepthOf(location.state);
    if (depth === 0) {
      normalizedRef.current = true; // nothing stale to unwind
    } else {
      // LEAVE normalizedRef false until the echo of the replace below lands.
      pendingNormalizeRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount
  }, []);

  // Replace the stale entry to navDepth 0 once the router is listening. This
  // must stay a passive effect: issued from the layout effect above, the
  // replace reaches browser history before BrowserRouter's own layout effect
  // has subscribed, no location change is delivered, and the reconciler's gate
  // never opens. The ref makes StrictMode's repeated effect a no-op.
  useEffect(() => {
    if (!pendingNormalizeRef.current) return;
    pendingNormalizeRef.current = false;
    const current = locationRef.current;
    navigateRef.current(current.pathname + current.search + current.hash, {
      replace: true,
      state: { ...(current.state as object | null), navDepth: 0 },
    });
  }, []);

  // Reconciler. Deps: [location] only (navigate read via ref) so it fires on
  // every genuine location change and nothing else.
  useLayoutEffect(() => {
    const browserDepth = navDepthOf(location.state);

    if (!normalizedRef.current) {
      // Gate: stay shut until the cold-mount replace's own echo (navDepth 0
      // against an empty stack) is what opens it. Never act on the stale read.
      if (browserDepth === 0 && coord.length() === 0) {
        normalizedRef.current = true;
      }
      return;
    }

    if (reconcilingRef.current) {
      // Echo of our own forward-neutralize navigate; consume it once.
      reconcilingRef.current = false;
      return;
    }

    const depth = coord.length();
    if (browserDepth === depth) {
      if (coord.isPopInFlight()) coord.clearPopInFlight();
      return;
    }
    if (browserDepth < depth) {
      coord.removeToDepth(browserDepth);
      if (coord.isPopInFlight()) coord.clearPopInFlight();
      return;
    }
    // browserDepth > depth: FORWARD into a dismissed overlay -> neutralize.
    reconcilingRef.current = true;
    navigateRef.current(-(browserDepth - depth));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- coord stable; navigate via ref
  }, [location]);

  return <NavStackContext.Provider value={coord}>{children}</NavStackContext.Provider>;
}
